import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, CheckCircleIcon, ClockIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  Sale,
  UpdateSaleDto,
  SaleItem,
  PaymentMethod,
  SaleStatus,
  getPaymentMethodDisplay,
  getSaleStatusColor,
  calculateSaleTotal,
} from '../../api/sales';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface EditSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateSaleDto) => Promise<void>;
  onStatusChange: (saleId: string, status: SaleStatus) => Promise<void>;
  sale: Sale | null;
}

const EditSaleModal: React.FC<EditSaleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onStatusChange,
  sale,
}) => {
  const [formData, setFormData] = useState<Omit<UpdateSaleDto, 'items'>>({
    invoiceNumber: '',
    saleDate: '',
    buyerName: '',
    buyerPhone: '',
    buyerAddress: '',
    paymentMethod: PaymentMethod.CASH,
    total: 0,
    notes: '',
  });

  const [items, setItems] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    if (sale && isOpen) {
      setFormData({
        invoiceNumber: sale.invoiceNumber,
        saleDate: sale.saleDate.split('T')[0],
        buyerName: sale.buyerName,
        buyerPhone: sale.buyerPhone,
        buyerAddress: sale.buyerAddress,
        paymentMethod: sale.paymentMethod,
        total: sale.total,
        notes: sale.notes || '',
      });
      setItems((sale.items || []).map(item => ({
        ...item,
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.unitPrice || 0),
        totalPrice: Number(item.totalPrice || 0),
      })));
    }
  }, [sale, isOpen]);

  const updateItemTotal = (index: number, quantity: number, unitPrice: number) => {
    const totalPrice = quantity * unitPrice;
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity,
      unitPrice,
      totalPrice,
    };
    setItems(updatedItems);
    
    const newTotal = calculateSaleTotal(updatedItems);
    setFormData(prev => ({ ...prev, total: newTotal }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      productName: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      
      const newTotal = calculateSaleTotal(updatedItems);
      setFormData(prev => ({ ...prev, total: newTotal }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading || !sale) return;
    
    try {
      setLoading(true);
      
      const submitData: UpdateSaleDto = {
        ...formData,
        items: items.filter(item => item.productName.trim() !== ''),
        saleDate: new Date(formData.saleDate!),
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error updating sale:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: SaleStatus) => {
    if (!sale || statusLoading) return;
    
    try {
      setStatusLoading(true);
      await onStatusChange(sale.id, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusIcon = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.PENDING:
        return <ClockIcon className="h-5 w-5" />;
      case SaleStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5" />;
      case SaleStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5" />;
      case SaleStatus.REFUNDED:
        return <ArrowPathIcon className="h-5 w-5" />;
      default:
        return <ClockIcon className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.PENDING:
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case SaleStatus.COMPLETED:
        return 'text-green-600 bg-green-100 border-green-200';
      case SaleStatus.CANCELLED:
        return 'text-red-600 bg-red-100 border-red-200';
      case SaleStatus.REFUNDED:
        return 'text-slate-600 bg-gray-100 border-gray-200';
      default:
        return 'text-slate-600 bg-gray-100 border-gray-200';
    }
  };

  if (!sale) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={`Edit Sale - ${sale.invoiceNumber}`} onClose={onClose} />
      <form id="edit-sale-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              {/* Status Management Section */}
              <div className="mb-6 p-4 bg-slate-50/80 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-slate-900">Sale Status</h4>
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(sale.status)}`}>
                      {getStatusIcon(sale.status)}
                      <span className="ml-1">{sale.status}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {sale.status === SaleStatus.PENDING && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleStatusChange(SaleStatus.COMPLETED)}
                          disabled={statusLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {statusLoading ? (
                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                          )}
                          Mark Complete
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStatusChange(SaleStatus.CANCELLED)}
                          disabled={statusLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {statusLoading ? (
                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 mr-1" />
                          )}
                          Cancel Sale
                        </button>
                      </>
                    )}
                    
                    {sale.status === SaleStatus.COMPLETED && (
                      <button
                        type="button"
                        onClick={() => handleStatusChange(SaleStatus.REFUNDED)}
                        disabled={statusLoading}
                        className="btn btn-secondary text-xs"
                      >
                        {statusLoading ? (
                          <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <ArrowPathIcon className="h-4 w-4 mr-1" />
                        )}
                        Process Refund
                      </button>
                    )}
                    
                    {(sale.status === SaleStatus.CANCELLED || sale.status === SaleStatus.REFUNDED) && (
                      <button
                        type="button"
                        onClick={() => handleStatusChange(SaleStatus.PENDING)}
                        disabled={statusLoading}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {statusLoading ? (
                          <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <ClockIcon className="h-4 w-4 mr-1" />
                        )}
                        Revert to Pending
                      </button>
                    )}
                  </div>
                </div>
                
                {sale.status !== SaleStatus.PENDING && (
                  <div className="mt-2 text-xs text-slate-500">
                    {sale.status === SaleStatus.COMPLETED && 'This sale has been completed and payment received.'}
                    {sale.status === SaleStatus.CANCELLED && 'This sale has been cancelled.'}
                    {sale.status === SaleStatus.REFUNDED && 'This sale has been refunded.'}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="invoiceNumber" className="block text-sm font-medium text-slate-700">
                      Invoice Number
                    </label>
                    <input
                      type="text"
                      id="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="saleDate" className="block text-sm font-medium text-slate-700">
                      Sale Date
                    </label>
                    <input
                      type="date"
                      id="saleDate"
                      value={
                        formData.saleDate instanceof Date 
                          ? formData.saleDate.toISOString().split('T')[0]
                          : formData.saleDate
                      }
                      onChange={(e) => setFormData(prev => ({ ...prev, saleDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="buyerName" className="block text-sm font-medium text-slate-700">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      id="buyerName"
                      value={formData.buyerName}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerName: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="buyerPhone" className="block text-sm font-medium text-slate-700">
                      Customer Phone
                    </label>
                    <input
                      type="tel"
                      id="buyerPhone"
                      value={formData.buyerPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerPhone: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="buyerAddress" className="block text-sm font-medium text-slate-700">
                      Customer Address
                    </label>
                    <textarea
                      id="buyerAddress"
                      value={formData.buyerAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, buyerAddress: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-slate-900">Sale Items</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-cyan-600 bg-cyan-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-5">
                        <label className="block text-sm font-medium text-slate-700">Product Name</label>
                        <input
                          type="text"
                          value={item.productName}
                          onChange={(e) => {
                            const updatedItems = [...items];
                            updatedItems[index].productName = e.target.value;
                            setItems(updatedItems);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                          placeholder="Enter product name..."
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => {
                            const quantity = parseInt(e.target.value) || 1;
                            updateItemTotal(index, quantity, item.unitPrice);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Unit Price</label>
                        <AmountInput
                          value={item.unitPrice}
                          onChange={(v) => updateItemTotal(index, item.quantity, v ?? 0)}
                          min={0}
                          inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Total</label>
                        <input
                          type="number"
                          value={Number(item.totalPrice || 0).toFixed(2)}
                          readOnly
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-slate-50/80 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="p-2 text-red-600 hover:text-red-800 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex justify-end">
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Total Amount</div>
                    <div className="text-xl font-bold text-slate-900">
                      R {formData.total?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  placeholder="Additional notes or comments..."
                />
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="edit-sale-form"
            disabled={loading || !formData.buyerName?.trim() || items.every(item => !item.productName.trim())}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditSaleModal; 